import { useAuth } from "@clerk/clerk-react";
import type {
	API_AcceptInvitationErrorCode,
	Role,
} from "@pilatestime/shared-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { AxiosError } from "axios";
import type { TFunction } from "i18next";
import {
	AlertCircle,
	CheckCircle,
	Clock,
	MailWarning,
	XCircle,
} from "lucide-react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
	useAcceptInvitation,
	validateInvitationQueryOptions,
} from "@/api/staff";
import { fetchCurrentUserQueryOptions } from "@/api/users";
import { Button } from "@/components/ui/button";
import { useApiParameters } from "@/hooks/useApiParameters";
import { getOrganizationRoleLabel } from "@/utils/roleUtils";
import { AppBaseLayout } from "../app/_app/-components/AppBaseLayout";

// ---------- Route ----------
export const Route = createFileRoute("/_auth/staff-invite/$token")({
	loader: async ({ context, params }) => {
		const { token: authToken, userId } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const invitationToken = params.token;

		context.queryClient.ensureQueryData(
			validateInvitationQueryOptions(invitationToken, authToken),
		);
		context.queryClient.ensureQueryData(
			fetchCurrentUserQueryOptions(userId, authToken),
		);

		return { authToken, invitationToken, userId };
	},
	component: StaffInvitationAcceptancePage,
});

function PageContainer({ children }: { children: React.ReactNode }) {
	return (
		<AppBaseLayout>
			<section className="container mx-auto px-4 py-16 min-h-[80vh] flex items-center justify-center">
				<div className="w-full max-w-lg">{children}</div>
			</section>
		</AppBaseLayout>
	);
}

function InfoCard({
	icon,
	iconBgClass,
	title,
	description,
	children,
}: {
	icon: React.ReactNode;
	iconBgClass: string;
	title: string;
	description?: React.ReactNode;
	children?: React.ReactNode;
}) {
	return (
		<div className="bg-base-100/90 border border-base-300/60 rounded-2xl shadow-xl p-8 text-center">
			<div
				className={`w-14 h-14 ${iconBgClass} rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm`}
			>
				{icon}
			</div>
			<h1 className="text-2xl font-semibold tracking-tight mb-2 text-base-content">
				{title}
			</h1>
			{description ? (
				<div className="text-sm text-base-content/80 mb-4 space-y-2">
					{description}
				</div>
			) : null}
			{children ? <div className="mt-4">{children}</div> : null}
		</div>
	);
}

function EmailMismatch({
	t,
	currentEmail,
	invitedEmail,
	onSwitchAccount,
	onGoHome,
}: {
	t: TFunction;
	currentEmail: string;
	invitedEmail: string;
	onSwitchAccount: () => void;
	onGoHome: () => void;
}) {
	return (
		<PageContainer>
			<InfoCard
				icon={<MailWarning className="w-8 h-8 text-warning" />}
				iconBgClass="bg-warning/10"
				title={t("invitations.emailMismatchTitle")}
				description={
					<>
						<p className="mb-2">
							{t("invitations.emailMismatchDescription", {
								currentEmail,
								expectedEmail: invitedEmail,
							})}
						</p>
						<p>{t("invitations.emailMismatchInstructions")}</p>
					</>
				}
			>
				<div className="flex flex-col gap-3 sm:flex-row">
					<Button
						variant="destructive"
						onClick={onSwitchAccount}
						className="w-full sm:flex-1"
					>
						{t("invitations.switchAccount")}
					</Button>
					<Button
						variant="default"
						onClick={onGoHome}
						className="w-full sm:flex-1"
					>
						{t("common.goHome")}
					</Button>
				</div>
			</InfoCard>
		</PageContainer>
	);
}

function InvalidOrUsedInvitation({
	t,
	friendlyMessage,
	onGoHome,
}: {
	t: TFunction;
	friendlyMessage: string;
	onGoHome: () => void;
}) {
	return (
		<PageContainer>
			<InfoCard
				icon={<XCircle className="w-8 h-8 text-error" />}
				iconBgClass="bg-error/10"
				title={t("invitations.invitationInvalid")}
				description={<p>{friendlyMessage}</p>}
			>
				<Button onClick={onGoHome} className="w-full text-primary-content">
					{t("invitations.goToDashboard")}
				</Button>
			</InfoCard>
		</PageContainer>
	);
}

function StaffInvitationAcceptancePage() {
	const { authToken, invitationToken, userId } = Route.useLoaderData();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { signOut } = useAuth();

	const acceptInvitationMutation = useAcceptInvitation();

	const { data: validation } = useSuspenseQuery(
		validateInvitationQueryOptions(invitationToken, authToken),
	);
	const { data: currentUser } = useSuspenseQuery(
		fetchCurrentUserQueryOptions(userId, authToken),
	);

	const invitedEmail = validation.invitation?.email ?? "";
	const currentEmail = currentUser?.email ?? "";
	const invitedRoleLabel = validation.invitation?.role
		? getOrganizationRoleLabel(validation.invitation.role as Role, t)
		: "";

	const emailsMatch = useMemo(
		() =>
			invitedEmail &&
			currentEmail &&
			invitedEmail.toLowerCase() === currentEmail.toLowerCase(),
		[invitedEmail, currentEmail],
	);

	const handleGoToDashboard = useCallback(() => {
		if (acceptInvitationMutation.data?.organizationId) {
			navigate({
				to: "/app/org/$orgId",
				params: { orgId: acceptInvitationMutation.data.organizationId },
			});
		} else {
			navigate({ to: "/app" });
		}
	}, [acceptInvitationMutation.data?.organizationId, navigate]);

	const handleSwitchAccount = useCallback(async () => {
		await signOut({ redirectUrl: window.location.href });
	}, [signOut]);

	const handleGoHome = useCallback(() => navigate({ to: "/app" }), [navigate]);

	const invalidFriendlyMessage = !validation.valid
		? (() => {
				switch (validation.message) {
					case "INVITATION_ALREADY_ACCEPTED":
						console.log("Already accepted");
						return t("invitations.alreadyAccepted");
					case "INVITATION_TOKEN_EXPIRED":
						console.log("Token expired");
						return t("invitations.tokenExpired");
					case "INVITATION_TOKEN_REVOKED":
						console.log("Token revoked");
						return t("invitations.tokenRevoked");
					case "INVITATION_ALREADY_MEMBER":
						console.log("Already member");
						return t("invitations.alreadyMember");
					case "INVITATION_NOT_FOUND":
						console.log("Not found");
						return t("invitations.invitationNotFound");
					case "INVITATION_INVALID":
						console.log("Invalid");
						return t("invitations.invitationInvalid");
					default:
						console.log("Unknown error:", validation.message);
						return validation.message;
				}
			})()
		: "";

	if (!validation.valid) {
		if (validation.message === "INVITATION_EMAIL_MISMATCH") {
			return (
				<EmailMismatch
					t={t}
					currentEmail={currentEmail}
					invitedEmail={invitedEmail}
					onSwitchAccount={handleSwitchAccount}
					onGoHome={handleGoHome}
				/>
			);
		}
		return (
			<InvalidOrUsedInvitation
				t={t}
				friendlyMessage={invalidFriendlyMessage}
				onGoHome={handleGoHome}
			/>
		);
	}

	// Email mismatch UI
	if (validation.valid && !emailsMatch) {
		return (
			<EmailMismatch
				t={t}
				currentEmail={currentEmail}
				invitedEmail={invitedEmail}
				onSwitchAccount={handleSwitchAccount}
				onGoHome={handleGoHome}
			/>
		);
	}

	// Processing acceptance
	if (acceptInvitationMutation.isPending) {
		return (
			<PageContainer>
				<InfoCard
					icon={
						<div className="loading loading-spinner loading-md text-primary" />
					}
					iconBgClass="bg-primary/10"
					title={t("invitations.processingInvitation")}
					description={<p>{t("invitations.pleaseWait")}</p>}
				/>
			</PageContainer>
		);
	}

	// Success state
	if (acceptInvitationMutation.isSuccess) {
		return (
			<PageContainer>
				<InfoCard
					icon={<CheckCircle className="w-8 h-8 text-success" />}
					iconBgClass="bg-success/10"
					title={t("invitations.invitationAccepted")}
				>
					<Button
						onClick={() => navigate({ to: "/app/select-organization" })}
						className="w-full"
					>
						{t("invitations.goToDashboard")}
					</Button>
				</InfoCard>
			</PageContainer>
		);
	}

	// Error state after trying to accept
	if (acceptInvitationMutation.isError) {
		const axiosError = acceptInvitationMutation.error as AxiosError;
		const errorData = (axiosError?.response?.data ?? {}) as {
			message?: string;
			errorCode?: API_AcceptInvitationErrorCode;
			expectedEmail?: string;
		};
		const errorMessage: string = errorData.message || axiosError?.message || "";
		const errorCode: API_AcceptInvitationErrorCode | undefined =
			errorData.errorCode;

		if (errorCode === "INVITATION_EMAIL_MISMATCH") {
			return (
				<EmailMismatch
					t={t}
					currentEmail={currentEmail}
					invitedEmail={invitedEmail}
					onSwitchAccount={handleSwitchAccount}
					onGoHome={handleGoHome}
				/>
			);
		}

		const isExpiredOrInvalid =
			errorMessage?.toLowerCase()?.includes("expired") ||
			errorMessage?.toLowerCase()?.includes("invalid");

		return (
			<PageContainer>
				<InfoCard
					icon={
						isExpiredOrInvalid ? (
							<XCircle className="w-8 h-8 text-error" />
						) : (
							<AlertCircle className="w-8 h-8 text-warning" />
						)
					}
					iconBgClass={isExpiredOrInvalid ? "bg-error/10" : "bg-warning/10"}
					title={
						isExpiredOrInvalid
							? t("invitations.invitationInvalid")
							: t("invitations.invitationError")
					}
					description={<p>{errorMessage}</p>}
				>
					<Button onClick={handleGoToDashboard} className="w-full">
						{t("invitations.goToDashboard")}
					</Button>
				</InfoCard>
			</PageContainer>
		);
	}

	// Default state - validated and ready to accept
	return (
		<PageContainer>
			<InfoCard
				icon={<Clock className="w-8 h-8 text-primary" />}
				iconBgClass="bg-primary/10"
				title={t("invitations.invitationDetails")}
				description={
					<div className="space-y-4 text-left">
						<p>{t("invitations.invitationIntro")}</p>
						<div className="grid gap-3">
							<div className="rounded-lg bg-base-200 px-3 py-2">
								<p className="text-xs font-semibold uppercase text-base-content/60">
									{t("invitations.email")}
								</p>
								<p className="text-sm font-medium break-all">{invitedEmail}</p>
							</div>
							<div className="rounded-lg bg-base-200 px-3 py-2">
								<p className="text-xs font-semibold uppercase text-base-content/60">
									{t("invitations.role")}
								</p>
								<p className="text-sm font-medium">
									{invitedRoleLabel || validation.invitation?.role}
								</p>
							</div>
						</div>
					</div>
				}
			>
				<Button
					className="w-full btn btn-primary text-primary-content"
					onClick={() =>
						acceptInvitationMutation.mutate({ invitationToken, authToken })
					}
					disabled={!emailsMatch}
				>
					{t("invitations.acceptInvitation")}
				</Button>
			</InfoCard>
		</PageContainer>
	);
}
